/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 */
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {
  AspectRatio,
  Box,
  Image,
  Skeleton,
  Text,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useBlogsPagesByTags} from '@amblique/king-living/app/contentful/hooks';
import {
  SEARCH_SPOTLIGHT_BLOG_LIMIT,
  getIdleBlogImageUrl,
  mapPageToSpotlightBlog,
} from '@amblique/king-living/app/components/search/search-spotlight-blog-helpers';
import {ensureArray} from '@amblique/king-living/app/utils/utils';

/**
 * Desktop-only column: articles from Header.searchTyping.articleTags (same query as Search idle spotlight).
 */
const SearchRecommendedForYouColumn = ({searchTyping, isHeaderLoading, onPickBlog}) => {
  const articleTagIds = useMemo(() => {
    return ensureArray(searchTyping?.articleTags)
      .map((t) => String(t).trim())
      .filter(Boolean);
  }, [searchTyping?.articleTags]);

  const {data: pagesCollection, isLoading: isBlogsByTagsLoading} = useBlogsPagesByTags(
    articleTagIds,
    {
      limit: SEARCH_SPOTLIGHT_BLOG_LIMIT,
      skip: 0,
      useRawArticleTagIds: true,
      queryOptions: {
        enabled: articleTagIds.length > 0,
      },
    }
  );

  const recommendedBlogs = useMemo(() => {
    const items = pagesCollection?.items;

    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    return items.map(mapPageToSpotlightBlog).filter(Boolean).slice(0, SEARCH_SPOTLIGHT_BLOG_LIMIT);
  }, [pagesCollection?.items]);

  const isListLoading = articleTagIds.length > 0 && isBlogsByTagsLoading;

  if (isHeaderLoading) {
    return (
      <Box
        data-testid="search-recommended-loading"
        flexShrink={0}
        maxW={{base: 'full', md: '291px'}}
        minW={0}
        width={{base: '100%', md: '291px'}}
      >
        <Skeleton height="10px" mb={3} width="200px" />
        <VStack align="stretch" spacing={6} width="full">
          {[0, 1].map((i) => (
            <Box key={i}>
              <AspectRatio ratio={16 / 9} width="full">
                <Skeleton height="full" width="full" />
              </AspectRatio>
              <Skeleton height="14px" mt={3} width="92%" />
            </Box>
          ))}
        </VStack>
      </Box>
    );
  }

  if (!articleTagIds.length) {
    return null;
  }

  const showBody = isListLoading || recommendedBlogs.length > 0;

  if (!showBody) {
    return null;
  }

  return (
    <Box
      data-testid="search-recommended-for-you"
      flexShrink={0}
      maxW={{base: 'full', md: '291px'}}
      minW={0}
      pl={{base: 0, md: 2}}
      width={{base: '100%', md: '291px'}}
    >
      <Text
        color="rgba(56, 56, 56, 0.60)"
        fontSize="10px"
        fontWeight="600"
        letterSpacing="0.08em"
        mb={3}
        textTransform="uppercase"
      >
        <FormattedMessage
          defaultMessage="Recommended for you"
          id="search.typing.recommended_heading"
        />
      </Text>
      {isListLoading && recommendedBlogs.length === 0 ? (
        <VStack align="stretch" spacing={6} width="full">
          {[0, 1].map((i) => (
            <Box key={i}>
              <AspectRatio ratio={16 / 9} width="full">
                <Skeleton height="full" width="full" />
              </AspectRatio>
              <Skeleton height="14px" mt={3} width="88%" />
            </Box>
          ))}
        </VStack>
      ) : null}
      {recommendedBlogs.length > 0 ? (
        <VStack align="stretch" spacing={6} width="full">
          {recommendedBlogs.map((blog, index) => {
            const name = blog?.title || blog?.pageTitle || '';
            const src = getIdleBlogImageUrl(blog);

            return (
              <Box
                key={blog?.pageSysId || blog?.pageSlug || blog?.sys?.id || `recommended-${index}`}
                _hover={{opacity: 0.92}}
                aria-label={name || 'Recommended article'}
                as="button"
                bg="transparent"
                border="none"
                cursor="pointer"
                m={0}
                p={0}
                tabIndex={0}
                textAlign="left"
                type="button"
                w="full"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPickBlog(blog);
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPickBlog(blog);
                }}
              >
                <Box borderRadius="sm" overflow="hidden" w="full">
                  <AspectRatio ratio={16 / 9} w="full">
                    {src ? (
                      <Image
                        alt={name || ''}
                        h="full"
                        objectFit="cover"
                        src={src}
                        w="full"
                      />
                    ) : (
                      <Box bg="blackAlpha.50" h="full" w="full" />
                    )}
                  </AspectRatio>
                </Box>
                <Text
                  as="span"
                  color="brandColors.darkCharcoal"
                  display="block"
                  fontSize="16px"
                  fontWeight="500"
                  lineHeight="130%"
                  mt={3}
                  noOfLines={4}
                  w="full"
                >
                  {name}
                </Text>
              </Box>
            );
          })}
        </VStack>
      ) : null}
    </Box>
  );
};

SearchRecommendedForYouColumn.propTypes = {
  isHeaderLoading: PropTypes.bool.isRequired,
  onPickBlog: PropTypes.func.isRequired,
  searchTyping: PropTypes.shape({
    articleTags: PropTypes.array,
  }),
};

export default SearchRecommendedForYouColumn;
